import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { RiArrowLeftSLine } from "react-icons/ri";
import classnames from "classnames";
import { TicketMessageType, useCreateUploadTokenMutation } from "../../../../__generated__/api";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { TicketItem } from "../../../../contexts/ticket-state";
import { UploadedFile } from "../../../agent/text-entry/text-entry";
import { ChatStateProvider } from "../../../chat/chat-state";
import Styles from "./Conversation.module.scss";
import TextEntry from "./TextEntry";
import Cards from "./cards";
import Button from "../../../theme/button/Button";

interface ConversationProps {
    ticket: TicketItem;
    isReadOnly: boolean;
    isViewPrivate: boolean;
}

export default function Conversation({ ticket, isReadOnly, isViewPrivate }: ConversationProps) {
    const [uploadAttachment] = useCreateUploadTokenMutation();
    const [currentFiles, setCurrentFiles] = useState<UploadedFile[]>([]);
    const [isScheduleDisplayed, setIsScheduleDisplayed] = useState(false);

    const { isLoading } = ticket.useChatDetails({ isTicketOnly: isReadOnly });

    useEffect(() => {
        if (!isLoading && ticket.isUnread) {
            ticket.markRead();
        }
    }, [ticket, isLoading, ticket.isUnread]);

    const uploadFile = async (file: File) => {
        const meta = await uploadAttachment({
            variables: {
                attachment: {
                    filename: file.name,
                    size: file.size,
                    mimeType: file.type,
                },
            },
        });

        if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
            return {
                name: file.name,
                mimeType: file.type,
                error: meta.data?.createUploadToken?.error ?? "Error uploading file",
            };
        }
        const { url, token } = meta.data.createUploadToken;

        let response;
        try {
            response = await fetch(url, {
                method: "POST",
                body: file,
                credentials: "include",
            });
        } catch (err) {
            LoggingHandler.instance.error({ err, name: file.name, type: file.type }, "Error uploading file");
            return {
                name: file.name,
                mimeType: file.type,
                error: "Error uploading file",
            };
        }

        if (response.status !== 201) {
            return {
                name: file.name,
                mimeType: file.type,
                error: "Error uploading file. Server did not return 201",
            };
        }

        const json = await response.json() as { url: string };

        return {
            name: file.name,
            mimeType: file.type,
            url: json.url,
            token,
        };
    };

    const onFilesAdded = useCallback(async (acceptedFiles: File[]) => {
        setCurrentFiles(current => (current ?? []).concat(acceptedFiles.map(file => ({
            name: file.name,
            mimeType: file.type,
            isUploading: true,
        }))));

        const files = await acceptedFiles.mapConcurrent(async (file: File): Promise<UploadedFile> => uploadFile(file));
        setCurrentFiles(current => (current ?? []).filter(row => !row.isUploading).concat(files));
    }, []);

    const allMessages = ticket.messages.dedupe(msg => msg.id);
    const messages = allMessages.filter(msg => msg.type !== TicketMessageType.Scheduled);
    const scheduled = allMessages.filter(msg => msg.type === TicketMessageType.Scheduled);

    useEffect(() => {
        if (isScheduleDisplayed && scheduled.isEmpty) {
            setIsScheduleDisplayed(false);
        }
    }, [scheduled, isScheduleDisplayed]);

    const {
        getRootProps, getInputProps, open: onAttachmentClicked, isDragActive,
    } = useDropzone({ onDrop: onFilesAdded, noClick: true, noKeyboard: true });
    return (
        <div className={Styles["conversation-container"]} {...getRootProps()}>
            <ChatStateProvider>
                {isDragActive && (
                    <div className={Styles["attachment-drop"]}>
                        Drag and Drop files here
                    </div>
                )}

                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <input {...getInputProps()} />

                {isScheduleDisplayed && (
                    <div className={classnames("text-font-header", Styles["scheduled-header"])}>
                        <Button
                            size="small"
                            variant="secondary"
                            prefixIcon={<RiArrowLeftSLine />}
                            onClick={() => setIsScheduleDisplayed(!isScheduleDisplayed)}
                        />
                        {" "}
                        Scheduled Messages
                    </div>
                )}

                <Cards
                    isEditable
                    isReadOnly={isReadOnly}
                    isViewPrivate={isViewPrivate}
                    ticketItem={ticket}
                    messages={isScheduleDisplayed ? scheduled : messages}
                    className={Styles["message-container"]}
                />

                <div className={Styles["text-container"]}>
                    <TextEntry
                        key={ticket.id}
                        onAttachmentClicked={onAttachmentClicked}
                        ticket={ticket}
                        onSetAttachments={setCurrentFiles}
                        attachments={currentFiles}
                        onRemoveAttachment={attachments => setCurrentFiles(attachments)}
                        onUploadAttachments={onFilesAdded}
                        scheduledMessages={isScheduleDisplayed ? [] : scheduled}
                        onToggleScheduledMessages={() => setIsScheduleDisplayed(!isScheduleDisplayed)}
                        isReadOnly={isReadOnly}
                    />
                </div>
            </ChatStateProvider>
        </div>
    );
}
